/**
 * Printer service — manages printer connections, print queue, and print jobs.
 *
 * Singleton-style service (plain object export, like billing.service.ts).
 * Uses the ESC/POS encoder + transports from lib/escpos/.
 */
import {
  UsbTransport,
  NetworkTransport,
  BrowserPrintTransport,
  buildReceipt,
  buildDuplicateReceipt,
  buildKot,
  buildTestReceipt,
  buildPaymentReceipt,
} from '@/lib/escpos';
import type {
  PrinterTransport,
  PrinterConfig,
  PrintJob,
  PrintResult,
  PrintJobType,
  ReceiptBillData,
  ReceiptTenantData,
  ReceiptLocationData,
  ReceiptOptions,
  ReceiptTaxPresentation,
  KotData,
  PaperWidth,
  ConnectionStatus,
  PaymentReceiptData,
} from '@/lib/escpos';
import type { CurrencyCode } from '@bbpos/types';
import { billingService } from './billing.service';

// ─── Storage key ────────────────────────────────────────────────────────────

const PRINTER_CONFIG_KEY = 'bbpos_printer_config';
const PRINT_QUEUE_KEY = 'bbpos_print_queue';

// ─── Internal state ─────────────────────────────────────────────────────────

let transport: PrinterTransport | null = null;
let processing = false;

// ─── Queue helpers ──────────────────────────────────────────────────────────

function loadQueue(): PrintJob[] {
  try {
    const raw = localStorage.getItem(PRINT_QUEUE_KEY);
    if (raw) {
      const jobs: PrintJob[] = JSON.parse(raw);
      // Re-hydrate Uint8Array from serialised array
      return jobs.map((j) => ({
        ...j,
        data: new Uint8Array(j.data as unknown as number[]),
      }));
    }
  } catch {
    // ignore corrupt data
  }
  return [];
}

function saveQueue(queue: PrintJob[]): void {
  try {
    // Uint8Array serialises as an object with numeric keys in JSON;
    // convert to plain array for reliable round-trip
    const serialisable = queue.map((j) => ({
      ...j,
      data: Array.from(j.data),
    }));
    localStorage.setItem(PRINT_QUEUE_KEY, JSON.stringify(serialisable));
  } catch {
    // localStorage may be full — silently fail
  }
}

function generateJobId(): string {
  return `pj_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
}

// ─── Transport factory ──────────────────────────────────────────────────────

function createTransport(config: PrinterConfig): PrinterTransport {
  switch (config.connectionType) {
    case 'usb':
      return new UsbTransport();
    case 'network':
      return new NetworkTransport(config.printerIp ?? '', config.printerPort);
    case 'browser':
    default:
      return new BrowserPrintTransport();
  }
}

// ─── Queue processor ────────────────────────────────────────────────────────

async function processQueue(
  onStatusChange?: (status: ConnectionStatus) => void,
  onQueueChange?: (queue: PrintJob[]) => void,
): Promise<void> {
  if (processing) return;
  processing = true;

  const queue = loadQueue();
  const pending = queue.filter((j) => j.status === 'queued');

  for (const job of pending) {
    if (!transport?.isConnected()) {
      processing = false;
      return;
    }

    job.status = 'printing';
    saveQueue(queue);
    onQueueChange?.(queue);

    try {
      for (let copy = 0; copy < job.copies; copy++) {
        await transport.write(job.data);
      }
      job.status = 'completed';
    } catch (err) {
      job.status = 'failed';
      job.error = err instanceof Error ? err.message : 'Print failed';
      onStatusChange?.('error');
    }

    saveQueue(queue);
    onQueueChange?.(queue);
  }

  // Clean up completed/failed jobs older than 5 minutes
  const cutoff = Date.now() - 5 * 60 * 1000;
  const cleaned = queue.filter(
    (j) => j.status === 'queued' || j.status === 'printing' || new Date(j.createdAt).getTime() > cutoff,
  );
  saveQueue(cleaned);
  onQueueChange?.(cleaned);

  processing = false;
}

// ─── Public API ─────────────────────────────────────────────────────────────

export const printerService = {
  /** Get the current transport instance. */
  getTransport(): PrinterTransport | null {
    return transport;
  },

  /** Connect to a printer using the given config. */
  async connect(
    config: PrinterConfig,
    onStatusChange?: (status: ConnectionStatus) => void,
  ): Promise<void> {
    // Disconnect any existing transport
    if (transport) {
      await transport.disconnect().catch(() => {});
    }

    transport = createTransport(config);
    onStatusChange?.('connecting');

    try {
      await transport.connect();
      onStatusChange?.('connected');
      // Save config on successful connection
      localStorage.setItem(PRINTER_CONFIG_KEY, JSON.stringify(config));
    } catch (err) {
      transport = null;
      onStatusChange?.('error');
      throw err;
    }
  },

  /** Disconnect current printer. */
  async disconnect(onStatusChange?: (status: ConnectionStatus) => void): Promise<void> {
    if (transport) {
      await transport.disconnect().catch(() => {});
      transport = null;
    }
    onStatusChange?.('disconnected');
  },

  /** Check if a printer is connected. */
  isConnected(): boolean {
    return transport?.isConnected() ?? false;
  },

  /** Get connection status. */
  getConnectionStatus(): ConnectionStatus {
    if (!transport) return 'disconnected';
    return transport.isConnected() ? 'connected' : 'disconnected';
  },

  /** Enqueue a print job and start processing. */
  enqueueJob(
    type: PrintJobType,
    data: Uint8Array,
    copies: number,
    referenceId?: string,
    onQueueChange?: (queue: PrintJob[]) => void,
    onStatusChange?: (status: ConnectionStatus) => void,
  ): PrintResult {
    const job: PrintJob = {
      id: generateJobId(),
      type,
      status: 'queued',
      data,
      copies,
      createdAt: new Date().toISOString(),
      referenceId,
    };

    const queue = loadQueue();
    queue.push(job);
    saveQueue(queue);
    onQueueChange?.(queue);

    // Start processing asynchronously
    processQueue(onStatusChange, onQueueChange);

    return { success: true, jobId: job.id };
  },

  /**
   * Print a bill receipt via ESC/POS. `tax` (optional) threads the tenant's
   * regime-aware vocabulary (GST/VAT, GSTIN/TRN) into the print payload —
   * omitted ⇒ India/GST defaults, byte-identical to legacy INR output.
   */
  printReceipt(
    bill: ReceiptBillData,
    tenant: ReceiptTenantData,
    location: ReceiptLocationData,
    options: ReceiptOptions,
    copies: number,
    onQueueChange?: (queue: PrintJob[]) => void,
    onStatusChange?: (status: ConnectionStatus) => void,
    tax?: ReceiptTaxPresentation,
  ): PrintResult {
    const data = buildReceipt(bill, tenant, location, options, undefined, tax);
    return this.enqueueJob('receipt', data, copies, bill.id, onQueueChange, onStatusChange);
  },

  /** Print a duplicate (reprint) receipt. See {@link printReceipt} for `tax`. */
  printDuplicate(
    bill: ReceiptBillData,
    tenant: ReceiptTenantData,
    location: ReceiptLocationData,
    options: ReceiptOptions,
    copies: number,
    onQueueChange?: (queue: PrintJob[]) => void,
    onStatusChange?: (status: ConnectionStatus) => void,
    tax?: ReceiptTaxPresentation,
  ): PrintResult {
    const data = buildDuplicateReceipt(bill, tenant, location, options, undefined, tax);
    return this.enqueueJob('duplicate', data, copies, bill.id, onQueueChange, onStatusChange);
  },

  /** L03: print the slip for a payment on account (job type `receipt`, reference = receipt id). */
  printPaymentReceipt(
    data: PaymentReceiptData,
    receiptId: string,
    paperWidth: PaperWidth,
    currency: CurrencyCode,
    copies: number,
    onQueueChange?: (queue: PrintJob[]) => void,
    onStatusChange?: (status: ConnectionStatus) => void,
  ): PrintResult {
    const bytes = buildPaymentReceipt(data, paperWidth, currency);
    return this.enqueueJob('receipt', bytes, copies, receiptId, onQueueChange, onStatusChange);
  },

  /** Print a KOT (kitchen order ticket). */
  printKot(
    kot: KotData,
    paperWidth: PaperWidth,
    copies: number,
    onQueueChange?: (queue: PrintJob[]) => void,
    onStatusChange?: (status: ConnectionStatus) => void,
  ): PrintResult {
    const data = buildKot(kot, paperWidth);
    return this.enqueueJob(
      kot.is_reprint ? 'kot-reprint' : 'kot',
      data,
      copies,
      kot.order_number,
      onQueueChange,
      onStatusChange,
    );
  },

  /** Print a test receipt. */
  printTest(
    paperWidth: PaperWidth,
    onQueueChange?: (queue: PrintJob[]) => void,
    onStatusChange?: (status: ConnectionStatus) => void,
  ): PrintResult {
    const data = buildTestReceipt(paperWidth);
    return this.enqueueJob('test', data, 1, undefined, onQueueChange, onStatusChange);
  },

  /** Kick the cash drawer (if connected to a USB/network printer). */
  async kickCashDrawer(): Promise<void> {
    if (!transport?.isConnected()) {
      throw new Error('Printer not connected');
    }
    const { EscPosEncoder } = await import('@/lib/escpos');
    const enc = new EscPosEncoder();
    enc.cashDrawer();
    await transport.write(enc.encode());
  },

  /**
   * Fallback: download PDF receipt via the existing server endpoint.
   * Used when printer type is 'browser' or no ESC/POS printer is configured.
   */
  async downloadReceiptPdf(
    billId: string,
    billNumber: string,
    format: '80mm' | '58mm' = '80mm',
  ): Promise<void> {
    const blob = await billingService.getReceipt(billId, format);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${billNumber}-receipt-${format}.pdf`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  /** Get the current print queue. */
  getQueue(): PrintJob[] {
    return loadQueue();
  },

  /** Clear all completed/failed jobs from the queue. */
  clearQueue(onQueueChange?: (queue: PrintJob[]) => void): void {
    const queue = loadQueue().filter((j) => j.status === 'queued' || j.status === 'printing');
    saveQueue(queue);
    onQueueChange?.(queue);
  },

  /** Retry all failed jobs. */
  retryFailed(
    onQueueChange?: (queue: PrintJob[]) => void,
    onStatusChange?: (status: ConnectionStatus) => void,
  ): void {
    const queue = loadQueue();
    for (const job of queue) {
      if (job.status === 'failed') {
        job.status = 'queued';
        job.error = undefined;
      }
    }
    saveQueue(queue);
    onQueueChange?.(queue);
    processQueue(onStatusChange, onQueueChange);
  },

  /** Load saved printer config from localStorage. */
  loadConfig(): PrinterConfig | null {
    try {
      const raw = localStorage.getItem(PRINTER_CONFIG_KEY);
      if (raw) return JSON.parse(raw);
    } catch {
      // ignore
    }
    return null;
  },

  /** Save printer config to localStorage. */
  saveConfig(config: PrinterConfig): void {
    localStorage.setItem(PRINTER_CONFIG_KEY, JSON.stringify(config));
  },
};
